import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Select,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  VStack,
  Button,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Collapse,
  Spinner,
  Code,
  Divider,
  useColorModeValue,
  useDisclosure,
  Icon,
  Input,
} from '@chakra-ui/react';
import { FiZap, FiPlay, FiChevronDown, FiChevronRight, FiUserCheck } from 'react-icons/fi';
import { useCampaignContext } from './CampaignContext';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { supabase } from '../../../../utils/supabase';
import CreatableSelect from './CreatableSelect';
import { useSchemaDiscovery } from '../../../../hooks/useSchemaDiscovery';
import useCalScheduling from '../../../../hooks/useCalScheduling';

function useDistinctSources() {
  const [sources, setSources] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const allSources = new Set();
        const pageSize = 1000;
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const { data } = await supabase
            .from('crm_deals')
            .select('source_medium')
            .not('source_medium', 'is', null)
            .range(offset, offset + pageSize - 1);
          (data || []).forEach(r => { if (r.source_medium) allSources.add(r.source_medium); });
          hasMore = data?.length === pageSize;
          offset += pageSize;
        }
        setSources(Array.from(allSources).filter(Boolean).map(s => ({
          value: s,
          label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
        })).sort((a, b) => a.label.localeCompare(b.label)));
      } catch (err) { console.warn('[useDistinctSources] Failed:', err); }
    })();
  }, []);
  return sources;
}

const TRIGGER_GROUPS = [
  {
    label: 'Contact Events',
    triggers: [
      { value: 'CONTACT_CREATED', label: 'Contact Created' },
      { value: 'CONTACT_UPDATED', label: 'Contact Field Changed' },
      { value: 'TAG_ADDED', label: 'Tag Added' },
      { value: 'TAG_REMOVED', label: 'Tag Removed' },
    ],
  },
  {
    label: 'Deal Events',
    triggers: [
      { value: 'DEAL_CREATED', label: 'Deal Created' },
      { value: 'PIPELINE_STAGE_CHANGED', label: 'Deal Stage Changed' },
      { value: 'DEAL_FIELD_UPDATED', label: 'Deal Field Updated' },
      { value: 'DEAL_ASSIGNED', label: 'Deal Assigned' },
      { value: 'DEAL_STALLED', label: 'Deal Stalled (No Activity)' },
    ],
  },
  {
    label: 'Engagement',
    triggers: [
      { value: 'DISPOSITION_LOGGED', label: 'Disposition Logged' },
      { value: 'EMAIL_OPENED', label: 'Email Opened' },
      { value: 'EMAIL_REPLIED', label: 'Email Replied' },
      { value: 'SMS_REPLIED', label: 'SMS Replied' },
    ],
  },
  {
    label: 'System',
    triggers: [
      { value: 'WEBHOOK_RECEIVED', label: 'Webhook Received' },
      { value: 'SCHEDULE_RECURRING', label: 'Recurring Schedule' },
      { value: 'SCHEDULE_DATE_FIELD', label: 'Date Field Trigger' },
      { value: 'MANUAL_ADD', label: 'Manual Add' },
    ],
  },
];

// Cal.diy scheduling pilot — appended only for flagged users
const MEETINGS_TRIGGER_GROUP = {
  label: 'Meetings',
  triggers: [
    { value: 'MEETING_BOOKED', label: 'Meeting Booked' },
    { value: 'MEETING_RESCHEDULED', label: 'Meeting Rescheduled' },
    { value: 'MEETING_CANCELLED', label: 'Meeting Cancelled' },
    { value: 'MEETING_NO_SHOW', label: 'Meeting No-Show' },
  ],
};

const MEETING_TRIGGER_VALUES = MEETINGS_TRIGGER_GROUP.triggers.map((t) => t.value);

// Triggers that don't apply to Company Automations: timed evaluation
// (processTimedTriggers) and manual/webhook paths are owner-scoped —
// they iterate a subscriber's own records or enroll from a user action.
export const AUTOMATION_EXCLUDED_TRIGGERS = new Set([
  'DEAL_STALLED',
  'SCHEDULE_RECURRING',
  'SCHEDULE_DATE_FIELD',
  'MANUAL_ADD',
  'WEBHOOK_RECEIVED',
]);

export default function TriggerHeader() {
  const { template, setTemplateMeta, testTrigger, triggerTestResult, testRunning, setTestContact, readOnly, isCompanyAutomation } = useCampaignContext();
  const { currentUser, SuperAdmin } = useAuth();
  const {
    dealStages,
    dispositions,
    getVisibleTags,
  } = useCrmSettings();
  const liveSourceOptions = useDistinctSources();
  const { getContactVariableGroups, getDealVariableGroups } = useSchemaDiscovery();
  const contactVariableGroups = getContactVariableGroups();
  const dealVariableGroups = getDealVariableGroups();
  const isAdmin = SuperAdmin || currentUser?.CRMRole === 'CRM Admin';
  const tagNameOptions = getVisibleTags(currentUser?.uid, isAdmin) || [];
  const payloadDisclosure = useDisclosure();
  const contextDisclosure = useDisclosure();
  const { enabled: calSchedulingEnabled } = useCalScheduling();

  const bg = useColorModeValue('blue.50', 'blue.900');
  const border = useColorModeValue('blue.200', 'blue.600');
  const resultBg = useColorModeValue('white', 'gray.700');

  const triggerType = template?.triggerType || 'MANUAL_ADD';
  const triggerConfig = template?.triggerConfig || {};

  // Show the Meetings group for flagged users, or when editing a campaign that
  // already uses a meeting trigger (so the select never shows a raw value).
  let triggerGroups = (calSchedulingEnabled || MEETING_TRIGGER_VALUES.includes(triggerType))
    ? [...TRIGGER_GROUPS, MEETINGS_TRIGGER_GROUP]
    : TRIGGER_GROUPS;

  if (isCompanyAutomation) {
    triggerGroups = triggerGroups
      .map((group) => ({
        ...group,
        triggers: group.triggers.filter((t) => !AUTOMATION_EXCLUDED_TRIGGERS.has(t.value)),
      }))
      .filter((group) => group.triggers.length > 0);
  }

  const updateConfig = (updates) => {
    setTemplateMeta({ triggerConfig: { ...triggerConfig, ...updates } });
  };

  // Contacts and deals share one tag vocabulary, so the picker no longer varies
  // by scope. tagScope still controls which event enrolls: a tag applied on a
  // deal emits deal.tag_added, one applied on a contact emits contact.tag_added.
  const tagScope = triggerConfig.tagScope || 'contact';

  return (
    <Box
      bg={bg}
      border="2px solid"
      borderColor={border}
      borderRadius="xl"
      p={5}
      w="100%"
      maxW="480px"
    >
      <Flex align="center" mb={3} gap={2}>
        <Icon as={FiZap} boxSize={5} color="blue.500" />
        <Text fontWeight="bold" fontSize="md">
          Trigger
        </Text>
      </Flex>

      <FormControl mb={3}>
        <FormLabel fontSize="sm">When this happens...</FormLabel>
        <Select
          size="sm"
          value={triggerType}
          isDisabled={readOnly}
          onChange={(e) =>
            setTemplateMeta({ triggerType: e.target.value, triggerConfig: {} })
          }
        >
          {triggerGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.triggers.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          ))}
        </Select>
      </FormControl>

      {triggerType === 'CONTACT_CREATED' && (
        <FormControl>
          <FormLabel fontSize="sm">From Source (optional)</FormLabel>
          <CreatableSelect
            options={liveSourceOptions}
            value={triggerConfig.source || ''}
            onChange={(val) => updateConfig({ source: val })}
            placeholder="Any source"
          />
        </FormControl>
      )}

      {triggerType === 'DEAL_CREATED' && (
        <FormControl>
          <FormLabel fontSize="sm">From Source (optional)</FormLabel>
          <CreatableSelect
            options={liveSourceOptions}
            value={triggerConfig.source || ''}
            onChange={(val) => updateConfig({ source: val })}
            placeholder="Any source"
          />
        </FormControl>
      )}

      {(triggerType === 'TAG_ADDED' || triggerType === 'TAG_REMOVED') && (
        <>
          <FormControl mb={2}>
            <FormLabel fontSize="sm">Tagged From</FormLabel>
            <Select
              size="sm"
              value={tagScope}
              isDisabled={readOnly}
              onChange={(e) => updateConfig({ tagScope: e.target.value })}
            >
              <option value="contact">Tagged on a contact</option>
              <option value="deal">Tagged on a deal</option>
              <option value="either">Either</option>
            </Select>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Contacts and deals share one tag list; this picks which screen the
              tag has to be applied from to enroll.
            </Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Tag Name</FormLabel>
            <CreatableSelect
              options={tagNameOptions}
              value={triggerConfig.tagName || ''}
              onChange={(val) => updateConfig({ tagName: val })}
              placeholder="Any tag"
            />
          </FormControl>
        </>
      )}

      {triggerType === 'PIPELINE_STAGE_CHANGED' && (
        <>
          <FormControl mb={2}>
            <FormLabel fontSize="sm">From Stage (optional)</FormLabel>
            <Select
              size="sm"
              value={triggerConfig.fromStage || ''}
              onChange={(e) => updateConfig({ fromStage: e.target.value })}
            >
              <option value="">Any Stage</option>
              {(dealStages || []).map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">To Stage</FormLabel>
            <Select
              size="sm"
              value={triggerConfig.toStage || ''}
              onChange={(e) => updateConfig({ toStage: e.target.value })}
            >
              <option value="">Any Stage</option>
              {(dealStages || []).map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {triggerType === 'DEAL_FIELD_UPDATED' && (
        <FormControl>
          <FormLabel fontSize="sm">Field Name (optional)</FormLabel>
          <Select
            size="sm"
            value={triggerConfig.fieldName || ''}
            onChange={(e) => updateConfig({ fieldName: e.target.value })}
          >
            <option value="">Any Field</option>
            {dealVariableGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.variables.map((v) => (
                  <option key={v.key} value={v.key.replace('deal.', '')}>{v.label}</option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FormControl>
      )}

      {triggerType === 'CONTACT_UPDATED' && (
        <FormControl>
          <FormLabel fontSize="sm">Field Name (optional)</FormLabel>
          <Select
            size="sm"
            value={triggerConfig.fieldName || ''}
            onChange={(e) => updateConfig({ fieldName: e.target.value })}
          >
            <option value="">Any Field</option>
            {contactVariableGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.variables.map((v) => (
                  <option key={v.key} value={v.key.replace('contact.', '')}>{v.label}</option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FormControl>
      )}

      {triggerType === 'DEAL_STALLED' && (
        <FormControl>
          <FormLabel fontSize="sm">No Activity For (Days)</FormLabel>
          <NumberInput
            size="sm"
            min={1}
            value={triggerConfig.stalledDays || 3}
            onChange={(_, val) => updateConfig({ stalledDays: val })}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>
      )}

      {MEETING_TRIGGER_VALUES.includes(triggerType) && (
        <FormControl>
          <FormLabel fontSize="sm">Event Type Slug (optional)</FormLabel>
          <Input
            size="sm"
            value={triggerConfig.eventTypeSlug || ''}
            isDisabled={readOnly}
            onChange={(e) => updateConfig({ eventTypeSlug: e.target.value.trim() })}
            placeholder="Any event type (e.g. intro-call)"
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            Only enroll bookings of this FHL Scheduling event type. Leave blank to
            match every meeting.
          </Text>
        </FormControl>
      )}

      {triggerType === 'DISPOSITION_LOGGED' && (
        <FormControl>
          <FormLabel fontSize="sm">Disposition (optional)</FormLabel>
          <Select
            size="sm"
            value={triggerConfig.disposition || ''}
            onChange={(e) => updateConfig({ disposition: e.target.value })}
          >
            <option value="">Any Disposition</option>
            {(dispositions || []).filter(d => d.value).map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </Select>
        </FormControl>
      )}

      {triggerType === 'SCHEDULE_RECURRING' && (
        <HStack spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm">Every</FormLabel>
            <NumberInput
              size="sm"
              min={1}
              value={triggerConfig.interval || 1}
              onChange={(_, val) => updateConfig({ interval: val })}
            >
              <NumberInputField />
            </NumberInput>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Period</FormLabel>
            <Select
              size="sm"
              value={triggerConfig.period || 'days'}
              onChange={(e) => updateConfig({ period: e.target.value })}
            >
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </Select>
          </FormControl>
        </HStack>
      )}

      {triggerType === 'SCHEDULE_DATE_FIELD' && (
        <>
          <FormControl mb={2}>
            <FormLabel fontSize="sm">Date Field</FormLabel>
            <Select
              size="sm"
              value={triggerConfig.dateField || ''}
              onChange={(e) => updateConfig({ dateField: e.target.value })}
            >
              <option value="">Select field...</option>
              <option value="birthday">Birthday</option>
              <option value="loan_anniversary">Loan Anniversary</option>
              <option value="estimated_closing_date">Est. Closing Date</option>
            </Select>
          </FormControl>
          <HStack>
            <FormControl>
              <FormLabel fontSize="sm">Days Before/After</FormLabel>
              <NumberInput
                size="sm"
                value={triggerConfig.offsetDays || 0}
                onChange={(_, val) => updateConfig({ offsetDays: val })}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Direction</FormLabel>
              <Select
                size="sm"
                value={triggerConfig.offsetDirection || 'before'}
                onChange={(e) => updateConfig({ offsetDirection: e.target.value })}
              >
                <option value="before">Before</option>
                <option value="after">After</option>
                <option value="on">On the Day</option>
              </Select>
            </FormControl>
          </HStack>
        </>
      )}
      {!readOnly && (
        <>
          <Divider my={3} borderColor={border} />

          <Button
            leftIcon={testRunning === 'trigger' ? <Spinner size="xs" /> : <Icon as={FiPlay} />}
            size="sm"
            colorScheme="blue"
            variant="outline"
            w="100%"
            onClick={testTrigger}
            isLoading={testRunning === 'trigger'}
            loadingText="Testing..."
          >
            Test Trigger
          </Button>

          {triggerTestResult && triggerTestResult.success !== false && (
            <Box mt={3} bg={resultBg} borderRadius="md" p={3} border="1px solid" borderColor={border}>
              <HStack mb={2}>
                <Badge colorScheme="green">{triggerTestResult.matchCount} matches</Badge>
                <Text fontSize="xs" color="gray.500">contacts would trigger this campaign</Text>
              </HStack>

              {triggerTestResult.sampleContacts?.length > 0 && (
                <Box overflowX="auto" mb={2}>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th fontSize="xs" px={2}>Name</Th>
                        <Th fontSize="xs" px={2}>Email</Th>
                        <Th fontSize="xs" px={2} />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {triggerTestResult.sampleContacts.slice(0, 5).map((c) => (
                        <Tr key={c.id}>
                          <Td fontSize="xs" px={2}>{c.firstName} {c.lastName}</Td>
                          <Td fontSize="xs" px={2} color="gray.500">{c.email || c.phone || '—'}</Td>
                          <Td fontSize="xs" px={2}>
                            <Button
                              size="xs"
                              colorScheme="blue"
                              variant="ghost"
                              leftIcon={<Icon as={FiUserCheck} boxSize={3} />}
                              onClick={() => setTestContact({
                                id: c.id,
                                firstName: c.firstName,
                                lastName: c.lastName,
                                email: c.email,
                                phone: c.phone,
                              })}
                            >
                              Use
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              <VStack align="stretch" spacing={1}>
                <Flex
                  align="center"
                  gap={1}
                  cursor="pointer"
                  onClick={payloadDisclosure.onToggle}
                  _hover={{ opacity: 0.7 }}
                >
                  <Icon as={payloadDisclosure.isOpen ? FiChevronDown : FiChevronRight} boxSize={3} />
                  <Text fontSize="xs" fontWeight="bold" color="gray.500">Sample Payload</Text>
                </Flex>
                <Collapse in={payloadDisclosure.isOpen}>
                  <Code fontSize="xs" whiteSpace="pre-wrap" p={2} borderRadius="md" w="100%" display="block">
                    {JSON.stringify(triggerTestResult.samplePayload, null, 2)}
                  </Code>
                </Collapse>

                {triggerTestResult.sampleContext && (
                  <>
                    <Flex
                      align="center"
                      gap={1}
                      cursor="pointer"
                      onClick={contextDisclosure.onToggle}
                      _hover={{ opacity: 0.7 }}
                    >
                      <Icon as={contextDisclosure.isOpen ? FiChevronDown : FiChevronRight} boxSize={3} />
                      <Text fontSize="xs" fontWeight="bold" color="gray.500">Hydrated Context Preview</Text>
                    </Flex>
                    <Collapse in={contextDisclosure.isOpen}>
                      <Code fontSize="xs" whiteSpace="pre-wrap" p={2} borderRadius="md" w="100%" display="block" maxH="200px" overflowY="auto">
                        {JSON.stringify({
                          contact: triggerTestResult.sampleContext.contact,
                          deal: triggerTestResult.sampleContext.deal,
                          sender: triggerTestResult.sampleContext.sender,
                        }, null, 2)}
                      </Code>
                    </Collapse>
                  </>
                )}
              </VStack>
            </Box>
          )}

          {triggerTestResult?.success === false && (
            <Box mt={2} p={2} bg="red.50" borderRadius="md" _dark={{ bg: 'red.900' }}>
              <Text fontSize="xs" color="red.600" _dark={{ color: 'red.300' }}>
                {triggerTestResult.error || 'Trigger test failed'}
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
